import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../lib/store';
import {
  LayoutDashboard,
  Ticket,
  Route,
  Truck,
  Map,
  History,
  BarChart3,
  RefreshCw,
  LogOut,
  ChevronRight,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

const ADMIN_NAV_ITEMS = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/admin/tickets', label: 'Bandeja de Tickets', icon: Ticket },
  { to: '/admin/rutas', label: 'Gestión de Rutas', icon: Route },
  { to: '/admin/vehiculos', label: 'Vehículos', icon: Truck },
  { to: '/admin/mapa', label: 'Mapa', icon: Map },
  { to: '/admin/historico', label: 'Histórico', icon: History },
  { to: '/admin/resumen-diario', label: 'Resumen Diario', icon: BarChart3 },
];

const SUPERVISOR_NAV_ITEMS = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/admin/tickets', label: 'Bandeja de Tickets', icon: Ticket },
  { to: '/admin/rutas', label: 'Gestión de Rutas', icon: Route },
  { to: '/admin/mapa', label: 'Mapa', icon: Map },
  { to: '/admin/resumen-diario', label: 'Resumen Diario', icon: BarChart3 },
];

// ─── GIS Sync Modal ───────────────────────────────────────────────────────────
const GisSyncModal = ({ open, onClose }) => {
  const [mode, setMode] = useState('import');
  const [dias, setDias] = useState(30);
  const [futureOnly, setFutureOnly] = useState(false);
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState({ current: 0, total: 0, percent: 0, success: 0, failed: 0 });
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [diagResult, setDiagResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const eventSourceRef = useRef(null);   // kept for export SSE
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    if (open) {
      fetchSyncStatus();
      setStatus('idle');
      setErrors([]);
      setMessage('');
      setProgress({ current: 0, total: 0, percent: 0, success: 0, failed: 0 });
      setDiagResult(null);
    }
    return () => {
      stopPolling();
      if (eventSourceRef.current) eventSourceRef.current.close();
    };
  }, [open, mode]);

  const fetchSyncStatus = async () => {
    try {
      const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      const tok = storedAuth?.state?.token;
      const res = await fetch(`${API}/api/arcgis/sync-status`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {}
      });
      const data = await res.json();
      setSyncStatus(data);
    } catch (_) {}
  };

  const startImport = async () => {
    setStatus('running');
    setErrors([]);
    setProgress({ current: 0, total: 0, percent: 0, success: 0, failed: 0 });
    setMessage('Iniciando importación...');

    try {
      const params = new URLSearchParams({ dias: String(dias), future_only: String(futureOnly) });
      const res = await fetch(`${API}/api/arcgis/import-start?${params}`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { job_id } = await res.json();

      // Poll every 2s
      pollRef.current = setInterval(async () => {
        try {
          const r = await fetch(`${API}/api/arcgis/import-status/${job_id}`);
          if (!r.ok) { stopPolling(); setStatus('error'); setMessage('Error al consultar estado del job'); return; }
          const data = await r.json();
          setMessage(data.message || '');
          setProgress({ current: data.current, total: data.total, percent: data.percent, success: data.success, failed: data.failed });
          if (data.status === 'done') {
            stopPolling(); setStatus('done');
            setErrors(data.errors || []);
          } else if (data.status === 'error') {
            stopPolling(); setStatus('error');
          }
        } catch (e) {
          stopPolling(); setStatus('error'); setMessage('Error de red al consultar el estado.');
        }
      }, 2000);
    } catch (e) {
      setStatus('error');
      setMessage(`No se pudo iniciar la importación: ${e.message}`);
    }
  };

  const startExport = () => {
    setStatus('running');
    setErrors([]);
    setProgress({ current: 0, total: 0, percent: 0, success: 0, failed: 0 });
    setMessage('');
    const storedAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
    const token = storedAuth?.state?.token;
    const url = `${API}/api/arcgis/sync-progress${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;
    es.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.status === 'start') {
        setMessage(data.message);
        if (data.total > 0) setProgress(prev => ({ ...prev, total: data.total }));
      } else if (data.status === 'progress') {
        setProgress({ current: data.current, total: data.total, percent: data.percent, success: data.success, failed: data.failed });
        setMessage(`Procesando ${data.current}/${data.total}...`);
      } else if (data.status === 'done') {
        setStatus('done'); setMessage(data.message); setErrors(data.errors || []);
        setProgress(prev => ({ ...prev, percent: 100, success: data.success, failed: data.failed }));
        es.close(); fetchSyncStatus();
      } else if (data.status === 'error') {
        setStatus('error'); setMessage(data.message); es.close();
      }
    };
    es.onerror = () => { setStatus('error'); setMessage('Error de conexión al exportar a GIS'); es.close(); };
  };

  const startSync = () => mode === 'import' ? startImport() : startExport();

  const runDiag = async () => {
    setDiagLoading(true); setDiagResult(null);
    try {
      const res = await fetch(`${API}/api/arcgis/test-connection`);
      setDiagResult(await res.json());
    } catch (e) {
      setDiagResult({ ok: false, results: { error: e.message } });
    } finally { setDiagLoading(false); }
  };

  const handleClose = () => {
    stopPolling();
    if (eventSourceRef.current) eventSourceRef.current.close();
    setStatus('idle');
    setProgress({ current: 0, total: 0, percent: 0, success: 0, failed: 0 });
    setMessage(''); setErrors([]);
    onClose();
  };

  const modeConfig = {
    import: { label: 'Importar desde GIS', color: 'bg-teal-600 hover:bg-teal-700' },
    export: { label: 'Exportar a GIS', color: 'bg-slate-700 hover:bg-slate-800' },
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="gis-sync-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <RefreshCw className="w-5 h-5 text-teal-600" />
            Sincronización con ArcGIS
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Mode Tabs */}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {['import', 'export'].map((m) => (
              <button key={m} onClick={() => { setMode(m); setStatus('idle'); setErrors([]); setMessage(''); }}
                disabled={status === 'running'}
                className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-all ${mode === m ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`gis-mode-${m}`}>
                {m === 'import' ? 'Importar desde GIS' : 'Exportar a GIS'}
              </button>
            ))}
          </div>

          {/* Import options */}
          {mode === 'import' && status === 'idle' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-3 border border-slate-200">
                <label className="text-sm text-slate-600 whitespace-nowrap">Recogidas futuras +</label>
                <select value={dias} onChange={e => setDias(Number(e.target.value))}
                  className="flex-1 text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white" data-testid="gis-import-dias">
                  <option value={7}>7 días atrás</option>
                  <option value={14}>14 días atrás</option>
                  <option value={30}>30 días atrás</option>
                  <option value={60}>60 días atrás</option>
                  <option value={90}>90 días atrás</option>
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600 px-1">
                <input type="checkbox" checked={futureOnly} onChange={e => setFutureOnly(e.target.checked)} className="rounded" />
                Solo recogidas futuras (ignora historial)
              </label>
            </div>
          )}

          {/* Export status */}
          {mode === 'export' && syncStatus && status === 'idle' && (
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-600">Tickets sincronizados</span>
                <span className="font-medium">{syncStatus.synced}/{syncStatus.total}</span>
              </div>
              <Progress value={syncStatus.total > 0 ? (syncStatus.synced / syncStatus.total) * 100 : 0} className="h-2" />
              <p className="text-xs text-slate-500 mt-2">
                {syncStatus.pending > 0 ? `${syncStatus.pending} ticket${syncStatus.pending > 1 ? 's' : ''} pendiente${syncStatus.pending > 1 ? 's' : ''}` : 'Todo sincronizado'}
              </p>
            </div>
          )}

          {/* Progress */}
          {(status === 'running' || status === 'done') && (
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{status === 'running' ? (mode === 'import' ? 'Importando...' : 'Exportando...') : 'Completado'}</span>
                <span className="font-medium">{progress.percent}%</span>
              </div>
              <Progress value={progress.percent} className="h-3" />
              <div className="flex gap-4 text-sm">
                <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> {progress.success} correctos</span>
                {progress.failed > 0 && <span className="text-red-500 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {progress.failed} errores</span>}
              </div>
              {message && <p className="text-xs text-slate-500 bg-slate-50 rounded p-2">{message}</p>}
            </div>
          )}

          {errors.length > 0 && (
            <div className="bg-red-50 rounded-lg p-3 border border-red-200 max-h-32 overflow-y-auto">
              <p className="text-xs font-medium text-red-700 mb-1">Registros con error:</p>
              {errors.map((err, i) => <p key={i} className="text-xs text-red-600">{err.globalid || err.ticket_id}: {err.error}</p>)}
            </div>
          )}

          {status === 'error' && (
            <div className="bg-red-50 rounded-lg p-3 border border-red-200 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <p className="text-sm text-red-700">{message}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            {status === 'idle' && (
              <Button onClick={startSync} className={`flex-1 ${modeConfig[mode].color}`} data-testid="gis-sync-start-btn"
                disabled={mode === 'export' && syncStatus?.pending === 0}>
                <RefreshCw className="w-4 h-4 mr-2" />
                {mode === 'import' ? 'Importar recogidas' : (syncStatus?.pending === 0 ? 'Todo sincronizado' : `Exportar ${syncStatus?.pending || ''} tickets`)}
              </Button>
            )}
            {status === 'running' && (
              <Button disabled className="flex-1">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {mode === 'import' ? 'Importando...' : 'Exportando...'}
              </Button>
            )}
            {(status === 'done' || status === 'error') && (
              <Button onClick={() => { setStatus('idle'); setErrors([]); setMessage(''); }} variant="outline" className="flex-1">
                <RefreshCw className="w-4 h-4 mr-2" /> Volver
              </Button>
            )}
            <Button variant="ghost" onClick={handleClose} data-testid="gis-sync-close-btn"><X className="w-4 h-4" /></Button>
          </div>

          {/* Diagnostics */}
          {(status === 'error' || status === 'idle') && (
            <div className="border-t border-slate-100 pt-3">
              <button onClick={runDiag} disabled={diagLoading}
                className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1.5 transition-colors" data-testid="gis-diag-btn">
                {diagLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertCircle className="w-3 h-3" />}
                Comprobar conectividad GIS
              </button>
              {diagResult && (
                <div className={`mt-2 rounded-lg p-2.5 text-xs border ${diagResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                  {diagResult.ok ? 'Conexión GIS correcta' : 'Error de conexión GIS:'}
                  {Object.entries(diagResult.results || {}).map(([k, v]) => <p key={k} className="mt-0.5 font-mono">{k}: {v}</p>)}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Admin Layout ────────────────────────────────────────────────────────
const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuthStore();
  const [showSyncModal, setShowSyncModal] = useState(false);

  const isAdmin = user?.role === 'admin' || !user?.role; // backwards compat
  const navItems = isAdmin ? ADMIN_NAV_ITEMS : SUPERVISOR_NAV_ITEMS;

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.to;
    return location.pathname.startsWith(item.to);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Admin Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_enseres-cadiz/artifacts/gxff3hzr_VALORIZA_color_positivo.png"
              alt="Valoriza"
              className="h-8"
            />
            <div className="hidden sm:block">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Panel de Control</p>
              <p className="text-sm font-semibold text-slate-800 leading-tight">Recogida de Enseres · Cádiz</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-sm font-medium text-slate-700">{user?.nombre || 'Admin'}</span>
              <span className="text-xs text-slate-400 capitalize">{user?.role || 'admin'}</span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-56 min-h-[calc(100vh-53px)] bg-white border-r border-slate-200 sticky top-[53px] h-[calc(100vh-53px)] flex flex-col">
          <nav className="flex-1 py-4 px-3 space-y-0.5">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group ${
                    active
                      ? 'bg-teal-50 text-teal-700 border-l-2 border-teal-600'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-teal-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
                  {item.label}
                  {active && <ChevronRight className="w-3 h-3 ml-auto text-teal-400" />}
                </Link>
              );
            })}
          </nav>

          {/* GIS Sync Button (admin only) */}
          {isAdmin && (
            <div className="p-3 border-t border-slate-200">
              <button
                onClick={() => setShowSyncModal(true)}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-teal-700 transition-all group"
                data-testid="gis-sync-btn"
              >
                <RefreshCw className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
                Sincronizar GIS
              </button>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 min-w-0">
          <Outlet />
        </main>
      </div>

      {/* GIS Sync Modal */}
      <GisSyncModal open={showSyncModal} onClose={() => setShowSyncModal(false)} />
    </div>
  );
};

export default AdminLayout;
